import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { hasMerchantPermission } from "../../api/merchantAuthApi";
import { cancelMerchantOrder, collectMerchantOrder, getMerchantOrder } from "../../api/merchantOrderApi";
import ModuleHeader, { ModuleHeaderAction, ModuleHeaderBack } from "../../components/ModuleHeader/ModuleHeader";
import ModulePageShell from "../../components/ModulePageShell/ModulePageShell";
import "./order-detail.scss";

const cancellationReasons = ["Out of stock", "Kitchen issue", "Store closed unexpectedly", "Product unavailable", "Customer requested cancellation", "Other"];
const baseTimelineSteps = ["Order Created", "Payment Successful", "Paid", "Ready for Pickup", "Collected"];
const inactiveTimelineSteps = ["Order Created", "Payment Successful", "Paid", "Cancelled", "Refunded"];
const statusClass = (value) => String(value || "").toLowerCase().replace(/\s+/g, "-");
const money = (value) => `RM${Number(value || 0).toFixed(2)}`;
const formatDate = (value) => value ? new Date(value).toLocaleString("en-MY", {
  day: "2-digit", month: "short", year: "numeric", hour: "numeric", minute: "2-digit",
}) : "—";

const InfoItem = ({ label, value }) => (
  <div className="order-detail__info-item"><span>{label}</span><strong>{value || "—"}</strong></div>
);

const OrderDetailPage = () => {
  const navigate = useNavigate();
  const { orderNo } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState(cancellationReasons[0]);
  const [cancelRemarks, setCancelRemarks] = useState("");
  const canManage = hasMerchantPermission("orders_collect");

  const loadOrder = async () => {
    setLoading(true);
    setError("");
    try {
      setOrder(await getMerchantOrder(orderNo));
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderNo]);

  const timelineMap = useMemo(() => {
    const result = {};
    (order?.timeline || []).forEach((entry) => {
      if (!result[entry.label]) result[entry.label] = formatDate(entry.at);
      if (entry.status === "paid") result["Payment Successful"] = formatDate(entry.at);
    });
    if (order?.paid_at) {
      result["Payment Successful"] ||= formatDate(order.paid_at);
      result.Paid ||= formatDate(order.paid_at);
    }
    if (order?.ready_at) result["Ready for Pickup"] ||= formatDate(order.ready_at);
    if (order?.completed_at) result.Collected ||= formatDate(order.completed_at);
    return result;
  }, [order]);

  if (loading) {
    return (
      <ModulePageShell className="order-detail">
        <div className="order-detail__panel">Loading order...</div>
      </ModulePageShell>
    );
  }
  if (!order) {
    return (
      <ModulePageShell className="order-detail">
        <div className="order-detail__panel">{error || "Order not found."}</div>
      </ModulePageShell>
    );
  }

  const inactive = ["cancelled", "refunded"].includes(order.status);
  const timelineSteps = inactive ? inactiveTimelineSteps : baseTimelineSteps;
  const canCollect = canManage && ["ready", "ready_for_pickup"].includes(order.status);
  const canCancel = canManage && order.status === "paid";

  const handleCollect = async () => {
    setSubmitting(true); setError(""); setSuccess("");
    try {
      const data = await collectMerchantOrder(order.order_number);
      setOrder(data);
      const waitingCount = Math.max(0, Number(localStorage.getItem("pickup_waiting_count") || 0) - 1);
      localStorage.setItem("pickup_waiting_count", String(waitingCount));
      window.dispatchEvent(new CustomEvent("pickup-waiting-count-updated", {
        detail: { count: waitingCount },
      }));
      setSuccess("Order marked as collected successfully.");
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async () => {
    setSubmitting(true); setError(""); setSuccess("");
    try {
      const data = await cancelMerchantOrder(order.order_number, { reason: cancelReason, remarks: cancelRemarks });
      setOrder(data);
      setIsCancelModalOpen(false);
      setSuccess("Order cancelled successfully. It is now available for admin refund review.");
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModulePageShell className="order-detail">
      <ModuleHeader
        bleed
        title={
          <>
            Order {order.order_number}
            <span className={`order-detail__status order-detail__status--${statusClass(order.status_label)}`}>
              {order.status_label}
            </span>
          </>
        }
        description="Review order details, customer information, payment status, and collection progress."
        back={
          <ModuleHeaderBack onClick={() => navigate("/orders")}>
            ← Back to Orders
          </ModuleHeaderBack>
        }
        actions={
          <ModuleHeaderAction onClick={() => window.print()}>Print Receipt</ModuleHeaderAction>
        }
      />

      {error && <div className="order-detail__inactive-banner order-detail__inactive-banner--cancelled"><strong>{error}</strong></div>}
      {success && <div className="order-detail__action-note"><strong>{success}</strong></div>}
      {inactive && <section className={`order-detail__inactive-banner order-detail__inactive-banner--${statusClass(order.status_label)}`}><strong>This order is no longer active.</strong><span>Pickup and payment actions are disabled because the order has been {order.status_label.toLowerCase()}.</span></section>}

      <section className="order-detail__summary-grid">
        <InfoItem label="Customer" value={`${order.customer} / ${order.customer_phone}`} />
        <InfoItem label="Merchant" value={order.merchant} />
        <InfoItem label="Pickup Window" value={order.pickup_window} />
        <InfoItem label="Total Amount" value={money(order.amount)} />
      </section>

      <section className="order-detail__panel order-detail__timeline-panel">
        <div className="order-detail__panel-title"><h3>Order Timeline</h3></div>
        <div className={`order-detail__timeline-bar order-detail__timeline-bar--${timelineSteps.length}`}>
          {timelineSteps.map((step, index) => {
            const done = Boolean(timelineMap[step]);
            const current = done && !timelineSteps.slice(index + 1).some((next) => timelineMap[next]);
            return <div className={`order-detail__timeline-step ${done ? "is-done" : ""} ${current ? "is-current" : ""}`} key={step}>
              <div className="order-detail__timeline-line" /><div className="order-detail__timeline-dot">✓</div><strong>{step}</strong><span>{timelineMap[step] || "—"}</span>
            </div>;
          })}
        </div>
      </section>

      <section className="order-detail__content-grid">
        <div className="order-detail__left-column">
          <div className="order-detail__panel">
            <div className="order-detail__panel-title"><h3>Order Items</h3></div>
            <div className="order-detail__items">
              <div className="order-detail__items-head"><span>Item</span><span>Qty</span><span>Price</span><span>Total</span></div>
              {order.items.map((item) => <div className="order-detail__items-row" key={item.id}><strong>{item.name}</strong><span>{item.quantity}</span><span>{money(item.price)}</span><span>{money(item.total)}</span></div>)}
            </div>
            <div className="order-detail__totals">
              <div><span>Subtotal</span><strong>{money(order.subtotal)}</strong></div>
              <div><span>Discount</span><strong>{money(order.discount)}</strong></div>
              <div className="order-detail__grand-total"><span>Grand Total</span><strong>{money(order.amount)}</strong></div>
              <div><span>Platform Fee</span><strong>{money(order.platform_fee)}</strong></div>
              <div className="order-detail__payout-total"><span>Payout Amount</span><strong>{money(order.payout_amount)}</strong></div>
            </div>
          </div>
          <div className="order-detail__panel"><div className="order-detail__panel-title"><h3>Customer & Pickup</h3></div><InfoItem label="Customer Email" value={order.customer_email} /><InfoItem label="Outlet" value={order.outlet} /></div>
        </div>

        <div className="order-detail__right-column">
          <div className="order-detail__panel order-detail__actions-panel">
            <div className="order-detail__panel-title"><h3>Actions</h3></div>
            <div className="order-detail__action-note"><strong>{canCollect ? "This order is ready for pickup." : "Action availability"}</strong><span>Collection is available for ready orders. Merchant cancellation is available while an order is Paid.</span></div>
            <button className="order-detail__primary-action" type="button" disabled={!canCollect || submitting} onClick={handleCollect}>Mark as Collected</button>
            <div className="order-detail__danger-zone"><span>Danger Zone</span><p>Cancel only when the paid order cannot be fulfilled. Admin will handle any refund review.</p><button type="button" disabled={!canCancel || submitting} onClick={() => setIsCancelModalOpen(true)}>Cancel Order</button></div>
          </div>
          <div className="order-detail__panel"><div className="order-detail__panel-title"><h3>Payment</h3></div><InfoItem label="Payment Status" value={order.payment_status} /><InfoItem label="Payment Method" value={order.payment_method} /><InfoItem label="Order Time" value={formatDate(order.created_at)} /></div>
          {inactive && <div className="order-detail__panel order-detail__cancelled-panel"><div className="order-detail__panel-title"><h3>{order.status === "refunded" ? "Refund Details" : "Cancellation Details"}</h3></div><InfoItem label="Cancelled By" value={order.cancelled_by} /><InfoItem label="Cancelled At" value={formatDate(order.cancelled_at)} /><InfoItem label="Details" value={order.cancellation_note} /><InfoItem label="Remarks" value={order.remarks} /></div>}
        </div>
      </section>

      {isCancelModalOpen && <div className="order-detail__modal-backdrop" role="presentation"><div className="order-detail__modal" role="dialog" aria-modal="true">
        <div className="order-detail__modal-header"><div><p className="order-detail__eyebrow">Merchant Cancellation</p><h3>Cancel Order {order.order_number}</h3></div><button type="button" onClick={() => setIsCancelModalOpen(false)}>×</button></div>
        <p className="order-detail__modal-message">This action cannot be undone. The order will be sent for admin refund review.</p>
        <label className="order-detail__field"><span>Cancellation Reason *</span><select value={cancelReason} onChange={(event) => setCancelReason(event.target.value)}>{cancellationReasons.map((reason) => <option key={reason}>{reason}</option>)}</select></label>
        <label className="order-detail__field"><span>Remarks</span><textarea rows="4" value={cancelRemarks} onChange={(event) => setCancelRemarks(event.target.value)} placeholder="Add optional remarks for admin review." /></label>
        <div className="order-detail__modal-actions"><button type="button" disabled={submitting} onClick={() => setIsCancelModalOpen(false)}>Back</button><button type="button" disabled={submitting} onClick={handleCancel}>{submitting ? "Cancelling..." : "Confirm Cancellation"}</button></div>
      </div></div>}
    </ModulePageShell>
  );
};

export default OrderDetailPage;
